import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';
import { api, API_KEY, NATIVE_API_BASE } from './api';

/**
 * Qvenari på Apple Watch. Klockan pratar själv med API:t (positioner, jaktchatt) med ett eget
 * token som den får härifrån, och skickar Tysta-kommandon hit eftersom radion (LiveKit) körs i
 * telefonens webbvy. Nativ del: ios/App/App/WatchBridgePlugin.swift.
 */

export type WatchCommand = 'toggleSpeaker' | 'toggleMic';

interface WatchStatus { supported: boolean; paired: boolean; installed: boolean; reachable: boolean; hasCredentials: boolean; device?: string }

const WatchBridge = registerPlugin<{
  status(): Promise<WatchStatus>;
  sendCredentials(options: { token: string; apiUrl: string; apiKey: string; userName: string }): Promise<{ sent: boolean }>;
  clearCredentials(): Promise<{ sent: boolean }>;
  setHuntState(options: { meetingId?: string; markId?: string; markName?: string; micOn?: boolean; speakerOn?: boolean; showTrail?: boolean }): Promise<{ sent: boolean }>;
  addListener(event: 'command', fn: (e: { cmd: WatchCommand }) => void): Promise<PluginListenerHandle>;
  addListener(event: 'credentialsRequested', fn: (e: { device?: string }) => void): Promise<PluginListenerHandle>;
  addListener(event: 'watchState', fn: (e: Partial<WatchStatus>) => void): Promise<PluginListenerHandle>;
  addListener(event: 'heartRate', fn: (e: { bpm: number }) => void): Promise<PluginListenerHandle>;
}>('WatchBridge');

/** Vems token klockan har — byts användaren ska klockan få ett nytt. */
const LINKED_EMAIL_KEY = 'qvenari_watch_linked_email';

export function isWatchBridgeAvailable(): boolean {
  const cap = (window as any).Capacitor;
  // Webben laddas från qvenari.com, så äldre appbyggen utan pluginet får samma kod.
  return !!cap?.isNativePlatform?.() && cap.getPlatform?.() === 'ios' && !!cap.isPluginAvailable?.('WatchBridge');
}

function linkedEmail(): string | null {
  try { return localStorage.getItem(LINKED_EMAIL_KEY); } catch { return null; }
}

let syncing: Promise<void> | null = null;

/** Ger klockan ett token om den har appen installerad och saknar token (eller har en annan användares). */
export function syncWatchCredentials(user: { email: string; name?: string }, force = false, device?: string): Promise<void> {
  if (!isWatchBridgeAvailable()) return Promise.resolve();
  if (syncing) return syncing;
  syncing = (async () => {
    try {
      const s = await WatchBridge.status();
      if (!s.installed) return;
      if (s.hasCredentials && !force && linkedEmail() === user.email) return;
      // Ett token per klocka: din egen och t.ex. hundens klocka loggar inte ut varandra.
      const token = await api.createWatchToken(device || s.device || undefined);
      const { sent } = await WatchBridge.sendCredentials({ token, apiUrl: NATIVE_API_BASE, apiKey: API_KEY, userName: user.name || user.email });
      if (sent) {
        try { localStorage.setItem(LINKED_EMAIL_KEY, user.email); } catch { /* ignore */ }
      }
    } catch (e) {
      console.warn('[WatchBridge] kunde inte koppla klockan', e);
    } finally {
      syncing = null;
    }
  })();
  return syncing;
}

/** Kör när användaren är inloggad i appen. Returnerar en funktion som tar bort lyssnarna. */
export function initWatchBridge(user: { email: string; name?: string }): () => void {
  if (!isWatchBridgeAvailable()) return () => {};
  void syncWatchCredentials(user);
  const handles = [
    WatchBridge.addListener('credentialsRequested', e => { void syncWatchCredentials(user, true, e.device); }),
    // Klockappen installerades efter att telefonappen startade.
    WatchBridge.addListener('watchState', e => { if (e.installed) void syncWatchCredentials(user); }),
  ];
  return () => { handles.forEach(h => h.then(x => x.remove()).catch(() => {})); };
}

/** Vid utloggning: klockan glömmer sitt token. (Tokenet återkallas nästa gång någon kopplar klockan.) */
export function clearWatchCredentials(): void {
  if (!isWatchBridgeAvailable()) return;
  try { localStorage.removeItem(LINKED_EMAIL_KEY); } catch { /* ignore */ }
  WatchBridge.clearCredentials().catch(() => {});
}

/** Jaktläget som klockan ska visa; null när ingen jakt pågår på telefonen. showTrail = "Visa eget spår". */
export function setWatchHuntState(state: { meetingId: string; markId: string; markName: string; micOn: boolean; speakerOn: boolean; showTrail: boolean } | null): void {
  if (!isWatchBridgeAvailable()) return;
  WatchBridge.setHuntState(state ?? {}).catch(() => {});
}

/** Tysta-kommandon från klockan. Lyssna bara medan radion är igång. */
export function onWatchCommand(fn: (cmd: WatchCommand) => void): () => void {
  if (!isWatchBridgeAvailable()) return () => {};
  const handle = WatchBridge.addListener('command', e => fn(e.cmd));
  return () => { handle.then(h => h.remove()).catch(() => {}); };
}

/** Pulsen från klockans träningspass under jakten, ungefär var femte sekund. */
export function onWatchHeartRate(fn: (bpm: number) => void): () => void {
  if (!isWatchBridgeAvailable()) return () => {};
  const handle = WatchBridge.addListener('heartRate', e => fn(e.bpm));
  return () => { handle.then(h => h.remove()).catch(() => {}); };
}
